import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type ConciergeSite = {
  id: string;
  org_id: string;
  user_id: string;
  title: string | null;
  elevenlabs_agent_id: string | null;
  advisor_name: string | null;
  theme: any;
  status: string;
  coach_user_id: string | null;
};

export type ConciergeResource = {
  id: string;
  site_id: string;
  type: "video" | "report" | "podcast" | "link" | string;
  title: string | null;
  description: string | null;
  url: string | null;
  storage_path: string | null;
  sort_order: number;
};

export async function getMyConciergeSite(): Promise<ConciergeSite | null> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData.user?.id;
  if (!uid) return null;
  const { data, error } = await supabase
    .from("concierge_sites" as any)
    .select("id, org_id, user_id, title, elevenlabs_agent_id, advisor_name, theme, status, coach_user_id")
    .eq("status", "published")
    .eq("user_id", uid)
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    console.warn("[concierge] getMyConciergeSite error", error);
    return null;
  }
  return ((data as unknown) as ConciergeSite) ?? null;
}

export async function getConciergeSiteById(siteId: string): Promise<ConciergeSite | null> {
  const { data, error } = await supabase
    .from("concierge_sites" as any)
    .select("id, org_id, user_id, title, elevenlabs_agent_id, advisor_name, theme, status, coach_user_id")
    .eq("id", siteId)
    .maybeSingle();
  if (error) {
    console.warn("[concierge] getConciergeSiteById error", error);
    return null;
  }
  return ((data as unknown) as ConciergeSite) ?? null;
}

export async function getSiteResources(siteId: string): Promise<ConciergeResource[]> {
  const { data, error } = await supabase
    .from("concierge_resources" as any)
    .select("id, site_id, type, title, description, url, storage_path, sort_order")
    .eq("site_id", siteId)
    .order("sort_order", { ascending: true });
  if (error) {
    console.warn("[concierge] getSiteResources error", error);
    return [];
  }
  return ((data as unknown) as ConciergeResource[]) ?? [];
}

export function useMyConciergeSite() {
  return useQuery({
    queryKey: ["concierge", "site", "me"],
    queryFn: getMyConciergeSite,
    staleTime: 60_000,
  });
}

export function useConciergeSiteById(siteId: string | undefined) {
  return useQuery({
    queryKey: ["concierge", "site", "by-id", siteId],
    queryFn: () => getConciergeSiteById(siteId as string),
    enabled: !!siteId,
    staleTime: 60_000,
  });
}


export function useConciergeResources(siteId: string | undefined) {
  return useQuery({
    queryKey: ["concierge", "resources", siteId],
    queryFn: () => getSiteResources(siteId as string),
    enabled: !!siteId,
    staleTime: 60_000,
  });
}
